import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import auth from './modules/auth'
import theme from './modules/theme'
import search from './modules/search'
import preferences from './modules/preferences'

Vue.use(Vuex)

const BASE_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5002/rightbuyme/us-central1'
  : 'https://us-central1-rightbuyme.cloudfunctions.net'

export default new Vuex.Store({
  modules: {
    auth,
    theme,
    search,
    preferences
  },
  state: {
    loading: false,
    error: null,
    searchResults: {
      query: '',
      products: [],
      hasMore: false
    },
    darkMode: false
  },
  mutations: {
    setLoading(state, loading) {
      state.loading = loading
    },
    setError(state, error) {
      state.error = error
    },
    setSearchResults(state, results) {
      try {
        // Handle null or undefined results
        if (!results) {
          state.searchResults = {
            query: '',
            products: [],
            hasMore: false
          };
          return;
        }

        // Get current timestamp once for consistency
        const timestamp = new Date().toISOString();

        // Ensure we have a valid products array
        const products = Array.isArray(results.products) ? results.products : [];
        
        // Map and validate each product
        const validatedProducts = products.map(product => {
          try {
            if (!product || typeof product !== 'object') {
              throw new Error('Invalid product data');
            }

            // Validate and normalize scores
            const scores = {
              price: normalizeScore(product.scores?.price),
              quality: normalizeScore(product.scores?.quality),
              ethical: normalizeScore(product.scores?.ethical),
              overall: normalizeScore(product.scores?.overall)
            };

            // Validate and normalize reasoning
            const reasoning = {
              price: validateText(product.reasoning?.price),
              quality: validateText(product.reasoning?.quality),
              ethical: validateText(product.reasoning?.ethical),
              political: validateText(product.reasoning?.political)
            };

            // Validate sources
            const sources = {
              reviews: Array.isArray(product.sources?.reviews) ? product.sources.reviews : [],
              ethics: Array.isArray(product.sources?.ethics) ? product.sources.ethics : []
            };

            // Validate retailers
            const retailers = Array.isArray(product.retailers) ? product.retailers.map(retailer => ({
              name: validateText(retailer.name),
              url: validateText(retailer.url),
              price: validateText(retailer.price),
              domain: validateText(retailer.domain),
              isManufacturerSite: !!retailer.isManufacturerSite
            })) : [];

            // Return complete product object with all required fields
            return {
              id: product.id || `product-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              name: validateText(product.name),
              manufacturer: validateText(product.manufacturer),
              price_range: validateText(product.price_range),
              image_url: validateUrl(product.image_url),
              scores,
              reasoning,
              sources,
              retailers,
              timestamp
            };
          } catch (error) {
            console.error('Product validation error:', error);
            return null;
          }
        }).filter(Boolean); // Remove any null products

        // Update state with new results
        if (results.loadMore) {
          // Append new products for load more
          state.searchResults = {
            query: results.query || state.searchResults.query,
            products: [...state.searchResults.products, ...validatedProducts],
            hasMore: results.hasMore
          };
        } else {
          // Replace results for new search
          state.searchResults = {
            query: results.query || '',
            products: validatedProducts,
            hasMore: results.hasMore
          };
        }
      } catch (error) {
        console.error('Error setting search results:', error);
        state.searchResults = {
          query: '',
          products: [],
          hasMore: false
        };
      }
    },
    toggleDarkMode(state, value) {
      if (typeof value === 'boolean') {
        state.darkMode = value
      } else {
        state.darkMode = !state.darkMode
      }
    }
  },
  actions: {
    async search({ commit, state }, { query, price, imageData, loadMore = false }) {
      commit('setLoading', true);
      commit('setError', null);

      try {
        const excludeProducts = loadMore ? state.searchResults.products.map(p => p.name) : [];
        
        const response = await axios.post(`${BASE_URL}/proxy`, {
          query,
          price,
          imageData,
          excludeProducts,
          loadMore
        });

        commit('setSearchResults', {
          ...response.data,
          loadMore
        });
      } catch (error) {
        console.error('Search error:', error);
        commit('setError', error.response?.data?.error || error.message);
        throw error;
      } finally {
        commit('setLoading', false);
      }
    },
    setError({ commit }, error) {
      commit('setError', error)
    }
  },
  getters: {
    isLoading: state => state.loading,
    hasError: state => !!state.error,
    errorMessage: state => state.error,
    searchResults: state => state.searchResults,
    isDarkMode: state => state.darkMode
  }
})

// Helper functions
function normalizeScore(score) {
  const num = Number(score);
  return !isNaN(num) ? Math.max(0, Math.min(10, num)) : 0;
}

function validateText(text) {
  return typeof text === 'string' ? text : '';
}

function validateUrl(url) {
  if (typeof url !== 'string') return '';
  try {
    new URL(url);
    return url;
  } catch {
    return '';
  }
} 